import React, { useState } from 'react';
import { Calendar, Repeat, Shield, ArrowRight } from 'lucide-react';
import { RecurringScheduleModal } from './RecurringScheduleModal';
import { track } from '../lib/analytics';

const Pillar = ({ Icon, title, body }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
    <div style={{
      flexShrink: 0,
      width: '36px', height: '36px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(194,168,120,0.07)',
      border: '1px solid rgba(194,168,120,0.22)',
      borderRadius: '2px',
    }}>
      <Icon size={15} style={{ color: '#C2A878' }} strokeWidth={1.4} />
    </div>
    <div>
      <p style={{ fontSize: '13px', color: '#F5F5F5', fontWeight: 500, marginBottom: '4px', letterSpacing: '0.3px' }}>{title}</p>
      <p style={{ fontSize: '13px', color: 'rgba(220,220,220,0.6)', fontWeight: 300, lineHeight: 1.6 }}>{body}</p>
    </div>
  </div>
);

/**
 * RecurringFamilyCTA — The "one-click" entry to the recurring family
 * schedule flow. Renders a luxury glass card with three trust pillars and
 * a single primary CTA that opens the focused booking modal.
 *
 * Variants:
 *   - "section"  : full-width section (Home page placement)
 *   - "compact"  : inline card (Fleet page placement)
 */
export const RecurringFamilyCTA = ({ variant = 'section', testIdPrefix = 'recurring-cta' }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    track('open_recurring_modal', { location: testIdPrefix, variant });
    setOpen(true);
  };

  if (variant === 'compact') {
    return (
      <>
        <div
          data-testid={`${testIdPrefix}-card`}
          className="fade-element"
          style={{
            padding: 'clamp(28px, 4vw, 44px)',
            backgroundColor: 'rgba(17,17,17,0.7)',
            border: '1px solid rgba(194,168,120,0.18)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(194,168,120,0.05)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{
            position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.4), transparent)',
          }} />

          <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: '40px', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <Repeat size={13} style={{ color: '#C2A878' }} strokeWidth={1.6} />
                <span style={{ fontSize: '10px', color: 'rgba(194,168,120,0.75)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 400 }}>
                  Recurring Family Schedule
                </span>
              </div>
              <h3 className="font-serif" style={{ fontSize: 'clamp(24px, 2.6vw, 30px)', fontWeight: 500, color: '#F5F5F5', lineHeight: 1.2, marginBottom: '14px' }}>
                Same chauffeur, every week.
              </h3>
              <p style={{ fontSize: '14px', color: 'rgba(220,220,220,0.62)', lineHeight: 1.75, fontWeight: 300, marginBottom: '24px' }}>
                Lock in a dedicated chauffeur and a fixed monthly rate for your school run, office commute or weekly airport trip.
              </p>
              <button
                data-testid={`${testIdPrefix}-open-btn`}
                onClick={handleOpen}
                style={{
                  backgroundColor: '#C2A878', color: '#0B0B0B', padding: '13px 28px',
                  fontSize: '12px', fontWeight: 500, letterSpacing: '1.4px', textTransform: 'uppercase',
                  border: 'none', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '10px',
                  transition: 'background-color 300ms ease, box-shadow 300ms ease',
                  boxShadow: '0 4px 24px rgba(194,168,120,0.18)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4BA8A'; e.currentTarget.style.boxShadow = '0 6px 32px rgba(194,168,120,0.32)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C2A878'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(194,168,120,0.18)'; }}
              >
                Reserve a Recurring Schedule
                <ArrowRight size={14} strokeWidth={1.6} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Pillar Icon={Repeat} title="One dedicated chauffeur" body="The same vetted driver your family already knows — every single ride." />
              <Pillar Icon={Shield} title="Locked monthly rate" body="No surge, no last-minute pricing. Predictable from the first invoice." />
              <Pillar Icon={Calendar} title="Set it & forget it" body="We hold your slot. You don't re-book each week." />
            </div>
          </div>
        </div>

        <RecurringScheduleModal open={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  // section variant — full Home page section
  return (
    <>
      <section
        data-testid={`${testIdPrefix}-section`}
        className="relative overflow-hidden section-depth"
        style={{
          backgroundColor: '#0B0B0B',
          padding: '10rem 1.5rem',
        }}
      >
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 75% 25%, rgba(194,168,120,0.06) 0%, transparent 55%), radial-gradient(ellipse at 15% 80%, rgba(194,168,120,0.03) 0%, transparent 55%)',
        }} />
        <div className="grain-overlay absolute inset-0" />

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: '80px', alignItems: 'center' }}>

            {/* LEFT — copy + primary CTA */}
            <div className="fade-element">
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <Repeat size={13} style={{ color: '#C2A878' }} strokeWidth={1.6} />
                <span style={{ fontSize: '11px', color: 'rgba(194,168,120,0.75)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 400 }}>
                  Recurring Family Schedule
                </span>
              </div>

              <h2 className="font-serif" style={{
                fontSize: 'clamp(30px, 3.6vw, 44px)',
                fontWeight: 500,
                color: '#F5F5F5',
                lineHeight: 1.15,
                marginBottom: '20px',
              }}>
                Tell us your week.<br />We&apos;ll match the same chauffeur.
              </h2>

              <div className="gold-divider" style={{ marginBottom: '24px' }} />

              <p style={{
                fontSize: '16px',
                color: 'rgba(220,220,220,0.7)',
                fontWeight: 300,
                lineHeight: 1.8,
                marginBottom: '32px',
                maxWidth: '480px',
              }}>
                School runs. Office commutes. Sunday flights to Pearson. Reserve a dedicated chauffeur once, and your family rides with the same trusted driver — every week, at a fixed rate.
              </p>

              <button
                data-testid={`${testIdPrefix}-open-btn`}
                onClick={handleOpen}
                style={{
                  backgroundColor: '#C2A878', color: '#0B0B0B', padding: '15px 32px',
                  fontSize: '12px', fontWeight: 500, letterSpacing: '1.4px', textTransform: 'uppercase',
                  border: 'none', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '12px',
                  transition: 'background-color 300ms ease, box-shadow 300ms ease, transform 200ms ease',
                  boxShadow: '0 4px 24px rgba(194,168,120,0.2)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4BA8A'; e.currentTarget.style.boxShadow = '0 6px 32px rgba(194,168,120,0.32)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C2A878'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(194,168,120,0.2)'; }}
              >
                Reserve a Recurring Schedule
                <ArrowRight size={14} strokeWidth={1.6} />
              </button>

              <p style={{ fontSize: '11px', color: 'rgba(220,220,220,0.4)', fontWeight: 300, marginTop: '18px', letterSpacing: '0.3px' }}>
                Takes 60 seconds. No charge today — the founder will call to confirm.
              </p>
            </div>

            {/* RIGHT — three pillars stacked */}
            <div className="fade-element" style={{
              padding: 'clamp(28px, 4vw, 44px)',
              backgroundColor: 'rgba(17,17,17,0.55)',
              border: '1px solid rgba(194,168,120,0.12)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.35), inset 0 1px 0 rgba(194,168,120,0.04)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.4), transparent)',
              }} />
              <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.55)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '28px' }}>
                How It Works
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <Pillar Icon={Repeat}   title="One dedicated chauffeur" body="The same vetted driver your family already knows — every single ride." />
                <Pillar Icon={Shield}   title="Locked monthly rate"     body="No surge, no last-minute pricing. Predictable from the first invoice." />
                <Pillar Icon={Calendar} title="Set it & forget it"      body="We hold your slot. You don't re-book each week." />
              </div>
            </div>

          </div>
        </div>
      </section>

      <RecurringScheduleModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};
